import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Waypoints, Mail, LayoutDashboard, UserRound, Building2, Radar, Inbox,
  MailPlus, MailSearch, Play, Settings as SettingsIcon, Crown, Webhook,
  ArrowRight, BookOpen, CheckCircle2, AlertTriangle, HelpCircle, Download,
} from "lucide-react";

const SECTIONS = [
  { id: "access", label: "Access & Sign-In", icon: Mail },
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "profile", label: "Profile & Resume", icon: UserRound },
  { id: "companies", label: "Manage Companies", icon: Building2 },
  { id: "discover", label: "Discover & Apply", icon: Radar },
  { id: "applications", label: "Applications Tracker", icon: Inbox },
  { id: "templates", label: "Email Templates", icon: MailPlus },
  { id: "audit", label: "Contact Audit", icon: MailSearch },
  { id: "loops", label: "Apply Series", icon: Play },
  { id: "settings", label: "Admin Settings", icon: SettingsIcon },
  { id: "billing", label: "Billing & Credits", icon: Crown },
  { id: "tracking-pipeline", label: "Email Tracking Pipeline", icon: Webhook },
];

const Section = ({ id, icon: Icon, title, children }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.45 }}
    data-testid={`docs-section-${id}`}
    className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 scroll-mt-24"
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-lg sm:text-xl font-semibold font-display">{title}</h2>
    </div>
    <div className="mt-4 text-sm sm:text-base text-slate-700 leading-relaxed space-y-4">{children}</div>
  </motion.section>
);

const Block = ({ icon: Icon, title, tint, children }) => (
  <div>
    <p className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${tint}`}>
      <Icon className="w-4 h-4" /> {title}
    </p>
    <ul className="mt-2 space-y-1.5">{children}</ul>
  </div>
);

const Li = ({ children }) => (
  <li className="flex items-start gap-2">
    <ArrowRight className="w-4 h-4 text-amber-500 mt-1 shrink-0" />
    <span>{children}</span>
  </li>
);

export default function Docs() {
  const [active, setActive] = useState("access");

  const jump = (id) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const location = useLocation();
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && SECTIONS.some((s) => s.id === hash)) {
      setActive(hash);
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location.hash]);

  const Features = ({ children }) => <Block icon={CheckCircle2} title="Features" tint="text-emerald-600">{children}</Block>;
  const Limits = ({ children }) => <Block icon={AlertTriangle} title="Limitations" tint="text-orange-500">{children}</Block>;
  const Scenarios = ({ children }) => <Block icon={HelpCircle} title="Scenarios" tint="text-sky-600">{children}</Block>;

  return (
    <div className="min-h-screen bg-[#F5F6F8] mesh-grid text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 h-16 px-4 sm:px-8 flex items-center justify-between border-b border-slate-200 bg-[#F5F6F8]/80 backdrop-blur-xl">
        <Link to="/" className="flex items-center">
          <img src="/logo-jbn.png" alt="JobBasket Network" className="h-8 w-auto object-contain" />
        </Link>
        <div className="flex items-center gap-3">
          <a
            href="/PlacementBridge-Pro-Documentation.pdf"
            download
            data-testid="docs-download-pdf"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-amber-300 text-amber-700 font-semibold hover:bg-amber-50 transition-colors"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>
          <Link to="/app" data-testid="docs-login-button"
            className="text-sm px-4 py-2 rounded-full bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors">
            Open App
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 grid lg:grid-cols-[240px_1fr] gap-8">
        {/* TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-400 px-2 mb-2 flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> Modules
            </p>
            <nav className="space-y-0.5">
              {SECTIONS.map((s) => (
                <button key={s.id} onClick={() => jump(s.id)} data-testid={`docs-nav-${s.id}`}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition-colors text-left ${
                    active === s.id ? "bg-amber-50 text-amber-700 font-semibold" : "text-slate-600 hover:bg-slate-50"
                  }`}>
                  <s.icon className="w-4 h-4 shrink-0" /> {s.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="space-y-6 min-w-0" data-testid="docs-page">
          <div>
            <h1 className="text-3xl font-bold font-display">Platform Guide</h1>
            <p className="text-slate-500 mt-2">Module-wise features, limitations, and real-world scenarios for candidates and admins.</p>
          </div>

          <Section id="access" icon={Mail} title="Access & Sign-In">
            <Features>
              <Li>Production sign-in happens from the main app (JobBasket) via secure SSO — one click and you land here already logged in.</Li>
              <Li>Admins are recognized automatically by their email (no separate admin account needed).</Li>
              <Li>A test login page with email OTP exists for preview/testing, restricted to allowlisted emails.</Li>
            </Features>
            <Limits>
              <Li>There is no public signup — accounts are created from the main app handoff.</Li>
              <Li>Direct email login is disabled in production by design.</Li>
            </Limits>
            <Scenarios>
              <Li>Candidate clicks "Candidate Connect" in the main app → lands on their dashboard, profile pre-filled where possible.</Li>
              <Li>Someone opens this app directly → they are redirected to the main app's sign-in page.</Li>
              <Li>Admin wants to test → uses the test login URL with their allowlisted email and sees extra sidebar items (Email Templates, Contact Audit, Settings).</Li>
            </Scenarios>
          </Section>

          <Section id="dashboard" icon={LayoutDashboard} title="Dashboard">
            <Features>
              <Li>At-a-glance stats: applications sent, credits remaining, saved companies, recent activity.</Li>
              <Li>Free-credit pill in the header shows remaining applications (or Pro status).</Li>
            </Features>
            <Limits>
              <Li>Stats reflect activity inside this app only.</Li>
            </Limits>
            <Scenarios>
              <Li>New candidate lands here first and is guided to complete their profile and save companies.</Li>
            </Scenarios>
          </Section>

          <Section id="profile" icon={UserRound} title="Profile & Resume">
            <Features>
              <Li>Captures every field application emails need: Full Name, Phone, Current Designation, Current Company, Years of Experience, LinkedIn/Portfolio, Key Achievement, Skills (first = Primary Skill), Location.</Li>
              <Li>Resume upload (PDF/DOCX/TXT) with instant text parsing and preview.</Li>
              <Li>Fields the main app provides are auto-filled on SSO login — and never overwrite anything you edit yourself.</Li>
              <Li>Empty mandatory fields are clearly marked "Not available" so you know exactly what to complete.</Li>
            </Features>
            <Limits>
              <Li>Auto-fill only covers fields the main app includes in its SSO token; anything else shows "Not available" until you fill it in.</Li>
              <Li>Email address is fixed to your login identity (read-only).</Li>
            </Limits>
            <Scenarios>
              <Li>Candidate tries to apply but a template needs their phone → Send is blocked with the exact missing field names and a link to this page.</Li>
              <Li>Main app sends skills and designation → those arrive pre-filled; candidate only adds their key achievement.</Li>
            </Scenarios>
          </Section>

          <Section id="companies" icon={Building2} title="Manage Companies">
            <Features>
              <Li>Browse the full client directory synced from the main app (search + industry/location/size filters, sortable columns, pagination).</Li>
              <Li>Save companies you care about; Saved Companies is your personal shortlist.</Li>
              <Li>Contact names are auto-linked from the directory to each company.</Li>
              <Li>Admins can re-sync the directory anytime with the "Sync Directory" button (incremental — only new/changed records).</Li>
            </Features>
            <Limits>
              <Li>The directory is read-only here — company data is mastered in the main app.</Li>
              <Li>A saved company shows jobs only when it also has a verified contact (see Discover & Apply).</Li>
            </Limits>
            <Scenarios>
              <Li>Candidate saves Blogic → Blogic's openings appear in Discover & Apply.</Li>
              <Li>Candidate un-saves a company → its jobs disappear from their board immediately.</Li>
            </Scenarios>
          </Section>

          <Section id="discover" icon={Radar} title="Discover & Apply">
            <Features>
              <Li>Split-view job board: filters on top (search, saved company, location, experience, work mode), jobs left, full detail right.</Li>
              <Li><strong>Saved + Verified only:</strong> you only see jobs from companies you saved that have a healthy, verified HR contact — so every "Apply" truly reaches someone.</Li>
              <Li>Apply modal shows batch size, credit cost (1), your live credit balance, and a template picker with template-category labels.</Li>
              <Li>Template content is auto-filled from your profile + the job and is locked (read-only) — no editing, no guesswork.</Li>
              <Li>On confirm, the email goes to the company's resolved contact and the application appears on your tracker instantly.</Li>
            </Features>
            <Limits>
              <Li>Jobs come from the main app's live feed; companies without a verified contact are hidden (fixable via Contact Audit pinning).</Li>
              <Li>One credit is spent per successfully sent application; queued applications (no contact) cost nothing.</Li>
            </Limits>
            <Scenarios>
              <Li>No saved companies → board shows a "Browse & Save Companies" prompt instead of jobs.</Li>
              <Li>Template needs a missing profile field → red error lists exact fields; Confirm stays disabled until fixed.</Li>
              <Li>Out of free credits → clear upgrade prompt; no silent failures.</Li>
            </Scenarios>
          </Section>

          <Section id="applications" icon={Inbox} title="Applications Tracker">
            <Features>
              <Li>Summary cards: Total Applied, Delivered, Opened, Replies.</Li>
              <Li>Table with Company, Role, Applied On, and live Delivery / Open / Reply status chips; search + status/time/location filters + pagination.</Li>
              <Li>Detail panel: recipient contact, sender email, template used, a visual timeline (Applied → Delivered → Opened → Reply), and Last Activity.</Li>
              <Li>Statuses update automatically via delivery webhooks — no refresh needed (auto-refresh every 30s).</Li>
            </Features>
            <Limits>
              <Li>"Opened" depends on the recipient's mail client allowing tracking; some inboxes block it.</Li>
              <Li>Reply detection is structured but awaits an inbound-reply channel — replies currently show "No Reply".</Li>
            </Limits>
            <Scenarios>
              <Li>Email bounces → chip flips to Bounced, timeline records it, and Contact Audit marks that address unhealthy.</Li>
              <Li>Recruiter opens your email → Opened chip and timeline update live within seconds.</Li>
            </Scenarios>
          </Section>

          <Section id="templates" icon={MailPlus} title="Email Templates (Admin)">
            <Features>
              <Li>Admins create/edit/activate/hide/delete templates with name, category, subject, and body.</Li>
              <Li>Dynamic variables auto-fill from candidate profile + job context: {"{CandidateName}, {JobTitle}, {CompanyName}, {PrimarySkill}, {YearsExperience}, {CurrentDesignation}, {CurrentCompany}, {PhoneNumber}, {LinkedInProfile}, {KeyAchievement}"} and more.</Li>
              <Li>Categories are creatable inline (e.g. General vs Follow-up) with a live filter + counts.</Li>
              <Li>One template can be marked Default/Recommended; sample-data preview before saving.</Li>
              <Li>Active + visible templates appear to candidates automatically; their last choice is remembered.</Li>
            </Features>
            <Limits>
              <Li>Candidates cannot edit template content — by design, messaging stays consistent.</Li>
              <Li>Variables without a profile source stay empty until the field exists (all 9 core variables are now editable in Profile).</Li>
            </Limits>
            <Scenarios>
              <Li>Admin adds a "Follow-up" category and templates → ready for the follow-up flow without code changes.</Li>
              <Li>Admin hides a template → it vanishes from the candidate picker instantly.</Li>
            </Scenarios>
          </Section>

          <Section id="audit" icon={MailSearch} title="Contact Audit & Health (Admin)">
            <Features>
              <Li>One-click audit of every synced contact: syntax check, mail-server (MX) verification, and bounce history — ~900 contacts in seconds.</Li>
              <Li>Health dashboard: Active, Needs Review (bounced/invalid/no mail server), Missing Email, Companies without contact.</Li>
              <Li>Per-company contact manager: see all contacts, who applications currently resolve to, and pin a Primary contact — or pin a custom email not in the directory.</Li>
              <Li>Smart auto-pick ranks contacts by role (Talent Acquisition &gt; HR Manager &gt; HR &gt; Founder) and demotes unhealthy emails automatically.</Li>
            </Features>
            <Limits>
              <Li>MX checks prove the domain can receive mail, not that a person reads it; deep inbox verification is out of scope.</Li>
              <Li>Audit runs on demand (scheduled runs can be added later).</Li>
            </Limits>
            <Scenarios>
              <Li>A company's synced email is stale → admin pins the correct HR email; every future application lands there.</Li>
              <Li>Weekly: admin runs the audit and fixes or pins anything newly flagged.</Li>
            </Scenarios>
          </Section>

          <Section id="loops" icon={Play} title="Apply Series">
            <Features>
              <Li>Automated application series: define a target role once and the discovery engine finds and applies to matching openings continuously, with auto-sync and match-based applications.</Li>
            </Features>
            <Limits>
              <Li>Requires the discovery-engine API key (set it in Settings → Apply Series Engine); until then it shows a "not connected" state.</Li>
            </Limits>
            <Scenarios>
              <Li>Admin pastes the API key in Settings and tests the connection → Apply Series goes live without a restart.</Li>
            </Scenarios>
          </Section>

          <Section id="settings" icon={SettingsIcon} title="Admin Settings">
            <Features>
              <Li>Runtime configuration without touching environment files: directory bridge URL &amp; secret, discovery-engine key, email key &amp; sender, payment keys, direct-login gate, admin emails, main-app URL.</Li>
              <Li>Saved overrides apply instantly and beat environment defaults; "Revert" restores the environment value.</Li>
              <Li>Secrets are always masked (last 4 chars); per-section Test Connection buttons verify live integrations.</Li>
            </Features>
            <Limits>
              <Li>Admin-only; unknown setting keys are rejected.</Li>
            </Limits>
            <Scenarios>
              <Li>Rotating a key → paste new value, test connection, done. No redeploy.</Li>
              <Li>Switching Razorpay from test to live at launch → replace both keys here.</Li>
            </Scenarios>
          </Section>

          <Section id="billing" icon={Crown} title="Billing & Credits">
            <Features>
              <Li>Every candidate gets 2 free applications; the header pill always shows the remaining balance.</Li>
              <Li>Pro upgrade via Razorpay checkout (UPI/cards) unlocks unlimited applications.</Li>
              <Li>Credits are spent only on successfully sent applications — queued or failed sends cost nothing.</Li>
            </Features>
            <Limits>
              <Li>Currently running on Razorpay test keys until live keys are set in Settings.</Li>
            </Limits>
            <Scenarios>
              <Li>Candidate spends both free credits → next apply shows the upgrade modal instead of failing silently.</Li>
            </Scenarios>
          </Section>

          <Section id="tracking-pipeline" icon={Webhook} title="Email Tracking Pipeline">
            <Features>
              <Li>Every application email is sent through our verified domain and carries an internal message ID.</Li>
              <Li>Delivery events (delivered / bounced / failed / opened) stream back via signed webhooks and update statuses, timeline, and summary counts automatically.</Li>
              <Li>Webhook processing is duplicate-safe and signature-verified.</Li>
            </Features>
            <Limits>
              <Li>Webhook events must be enabled in the email provider dashboard pointing to this app's webhook URL.</Li>
              <Li>Tracking relies on the provider; delays of a few seconds are normal.</Li>
            </Limits>
            <Scenarios>
              <Li>Admin rotates the webhook signing secret in Settings → verification keeps working with zero downtime.</Li>
            </Scenarios>
          </Section>
        </main>
      </div>
    </div>
  );
}
